import { stateRef, getFrameworkVar, getFrameworkServiceUrl } from './states'
import { actions, fetchIamCustomRoleDefinitionAndCache } from './actions'
import { dedupPromise, isNonemptyArray, jwtDecode } from './utils'
import { tsgUserManagers, clearCurrentTsgId, getTsgAccess as getTsgAccessFn } from './tsg'
import { // only pick some from utils, not expose everything
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} from '../containers/mainOneappNew/fawkesUtils'
import extensionPoints, { extPointsExports } from '../extpoints'
import { indexCustomRoles } from './access'
import cache from './cache'
import { __error, __isDebugOn, __isLocalhost } from '../init/debug'
import { _performance } from '../init/performance'
import preference from './preference'

import type { NavItem } from './microAppTypes'
import type { FeatureInfo, IamCustomRolesDefinition, ReadonlyAccess } from './stateTypes'

export type { History } from 'history'
export type { Pendo } from './pendo'
export type { FetchWithTimeout, RequestInitWithTimeout, RequestInit } from '../libs/fetch'
export type { StoreState, StateRef, AuthState, MainState, GlobalStore, GlobalStore as Store, FeatureFlagOptions, ServiceOptions, ParsedPrn, Authed, AuthInfo, FeatureInfo, FawkesTenantInfo, UserState, ConfigLocation, LoggedInUser, AppRegion, AppManifests, AppManifest, IamPermission, IamPermissions, IamRoleDefinition, ReadyonlyIndexedArray, ReadonlySetArray, StringSetArray as StringArraySet, Vars, ReadonlyAccess, IndexedArray, MarketplaceContent, DeveloperDefinedField, SupportAccount, ThemeValue, ThemePreferenceValue, TsgProperty, ReadonlyTsgProperties, FrameworkVars } from './stateTypes'
export type { Instance, Tsg, ReadonlyTsgTree, TsgTree, ReadonlyTsgInstances } from './indexedArrays'
export type { AsyncCache, SyncCache, CacheOptions } from './cache'
export type { NavItem, NavAction, NavParams, MicroAppConfig, MicroApp, MicroAppExport, SparkySettings, SparkyAvailFunc, Exposes, AvailFunc, AvailParam, LazyFactory, OverrideLeftNavOption, NavBadgeProps, Extensions, ExtensionPoints, ExtensionsConfig, ExtensionsExports, ExtensionPointsExports } from './microAppTypes'
export type { TsgLoadFnParam, TsgLoadFn } from './newMicroAppTypes'
export type { Entitlements, Credentials } from './TokenManagerTypes'
export type { Contribution, ExtensionConfig, ExtensionManager, ExtensionPoint, ValidationResult, RuntimeValidator } from './ExtensionTypes'
export type { TokenManager } from '@sparky/token-manager'

export const fawkesUtils = {
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  jwtDecode,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} as {
  doLogin: () => void,
  doLogout: () => void,
  getAuthHeaders: () => Record<string, string>,
  getCSRFToken: () => string,
  getMd5Salt: () => string,
  jwtDecode: typeof jwtDecode,
  getParam: (arg: string) => string | undefined,
  getSession: () => string | undefined,
  getRedirectURL: () => string,
  getAdminClusterURL: () => string,
  getFawkesTenantId: () => string | undefined,
  checkIsFeatureEnabled: (list?: FeatureInfo[], key?: string) => boolean,
  isValidRoute: (nav: NavItem) => boolean,
}

/**
 * @deprecated use fawkesUtils instead
 */
export { fawkesUtils as utils }

export const _resetAuth = (): void => {
  clearCurrentTsgId()
  actions.logout()
  setTimeout(() => {
    localStorage.clear()
    sessionStorage.clear()
  }, 100)
}

export const _getPerformanceTiming = () => _performance.timing
export const _setPreviewOverrides = (microAppIds: string | string[], overrides: any[], tsgId?: string | boolean): void => {
  const overridable = getFrameworkVar('overridable') && !__isLocalhost
  if (overridable && !microAppIds && !overrides) {
    console.info('clear preview overrides from session storage')
    sessionStorage.removeItem('#sparky-micro-apps')
    sessionStorage.removeItem('#sparky-overrides')
    if (window.location.hash) {
      window.location.hash = ''
    }
    console.warn('please reload the page to take effect')
    return
  }
  const microApps = Array.isArray(microAppIds) ? microAppIds.join(',') : microAppIds || ''
  const overrideJson = isNonemptyArray(overrides) ? JSON.stringify(overrides) : ''
  if (!microApps && !overrideJson) {
    console.warn('please provide a list of micro-app ids and/or overrides')
    return
  }
  const url = new URL(window.location.href)
  if (overridable) {
    sessionStorage.setItem('#sparky-micro-apps', microApps)
    sessionStorage.setItem('#sparky-overrides', overrideJson)
    console.info('set preview overrides into session storage\nmicro-app ids: ', microApps, '\noverrides:', overrideJson ? overrides : '(none)')
  }
  else { // guess the preview url using scm or api gateway url
    const scmBaseUrl = getFrameworkServiceUrl('netsec_portal')
    if (scmBaseUrl && /^https:\/\/stratacloudmanager\.(?:dev|qa)\.appsvc.paloaltonetworks\.com/.test(scmBaseUrl)) {
      url.host = scmBaseUrl.replace('https://stratacloudmanager.', 'stratacloudmanager-preview.')
    }
    else {
      const apiGwEnv = getFrameworkServiceUrl('api_gateway')?.match(/^https:\/\/(dev|qa)\.api\./)?.[1]
      if (apiGwEnv) {
        url.host = `stratacloudmanager-preview.${apiGwEnv}.appsvc.paloaltonetworks.com`
      }
      else {
        console.warn('the current sparky shell is not overridable')
        return
      }
    }
    console.info('generate preview url for\nmicro-app ids: ', microApps, '\noverrides:', overrideJson ? overrides : '(none)')
  }

  if (tsgId) {
    url.searchParams.set('tsg_id', tsgId === true ? stateRef.current.auth?.tsg_id || '' : tsgId)
  }
  const params = new URLSearchParams()
  if (microApps) {
    params.set('sparky-micro-apps', microApps)
  }
  if (overrideJson) {
    params.set('sparky-overrides', overrideJson)
  }
  url.hash = `#${params}`
  if (overridable) {
    console.info('the preview overrides will be forgotten after close the browser tab, but can be revisit using the url:', url.toString())
    console.warn('please reload the page to take effect')
  }
  else {
    console.warn('the current sparky shell is not overridable, please visit the preview url to see the preview effect:', url.toString())
  }
}

export const getTsgAccess = (tsg_id: string): Promise<ReadonlyAccess | undefined> => {
  const fn = async () => {
    let definitions = stateRef.current.auth?.rolePermissionDefinition
    if (definitions && definitions.customRoles?.tsg_id !== tsg_id) {
      const cacheKey = `custom-roles-${tsg_id}`
      const cached = await cache.get<IamCustomRolesDefinition>(cacheKey)
      if (cached) {
        Object.assign(indexCustomRoles(cached), { tsg_id })
        definitions = { ...definitions, customRoles: cached }
      }
      else {
        const customRoles = await fetchIamCustomRoleDefinitionAndCache(tsg_id)
        definitions = { ...definitions, customRoles }
      }
    }
    const access = await getTsgAccessFn(tsg_id, definitions)
    return access
  }
  return dedupPromise(`tsg_access:${tsg_id}`, fn)
}

Object.assign(extensionPoints, extPointsExports)

Object.defineProperties(tsgUserManagers, {
  getCurrentTsgId: {
    value: (): string | undefined => {
      return stateRef.current.auth?.tsg_id || tsgUserManagers.__current_tsg_id__
    }
  }
})

export const _getTsgUserManagers = () => {
  if (__isDebugOn()) {
    return tsgUserManagers
  }
  console.error('this method is not allowed')
}

export const _getPreferenceClient = () => {
  if (__isDebugOn()) {
    return preference
  }
  console.error('this method is not allowed')
}

/**
 * @deprecated AuthInfoSpinner is no longer supported
 */
export const AuthInfoSpinner = () => {
  __error('AuthInfoSpinner is no longer supported')
  return null
}

/**
 * @deprecated micro-app should handle its own translation
 */
export const _T = (str: string): string => str

export { __isDebugOn, actions, stateRef as storeStateRef, extensionPoints, extensionPoints as points }
export { SPARKY_FRAMEWORK } from './constants'
export * as hooks from './hooks'
export * as layout from '../containers/layout'
export { store, dispatch, getState, getStateUpdated, getAuthState, getMainState, events, history, onAuthed, inited, authed, loaded, authenticated, getInitSettings, getFrameworkVar, getFrameworkVars, getFrameworkServiceUrl, getCurrentPlatformRegion, isOidcClient, isAppCanBeLaunched } from './states'
export { prn, createUrnUtil } from './access'
export { getExtensions } from './extensions/init'
export { getRouteExtension, getFeatureFlag, getServiceUrl, getPropertyValue, getPropertyValueAsync } from './extensions/utils'
export { hasMicroApp, hasMicroAppExported, hasExposedNamespace, getExposedNamespace, getMicroAppVars, importExposed, lazyImportComponent, getExposedSync } from './microApps'
export { hasTsgSupport, getTsgToken, getCurrentTsgId, getCurrentTsgToken, getTsgInfo, revokeTsgToken, getTsgIdFromUrl, cleanupTsgIdFromUrl } from './tsg'
export { indexTsgTree, indexTsgInstances, indexAppManifests, indexSupportAccounts } from './indexedArrays'
export { getCurrentTsgDebugInfo } from './debugInfo'
export { getPerformanceTiming } from '../init/performance'
export { getDatadogRum, getDatadogLogs, getPendo } from './externals'
export { ReactBridge, createReactBridge, importReactBridgeComponent } from '../containers/common/ReactBridge'
export { GlobalStateContext, useStoreSelector } from '../containers/common/storeHook'
export { default as i18n, getGridLocale, getUserLanguage } from '../i18n'
export { default as StartupManager } from '../containers/mainOneappNew/StartupManager'
export { importLibs, moduleImport } from '../libs/base'
export { getAuthData, getToken, getTokenManager } from '../containers/mainOneappNew/TsgTkmProvider'
export { default as LoadingPlaceholder } from '../containers/common/LoadingPlaceholder'
export { loadJs, loadCss, clearLoadingMask, getRootElement, dedupPromise, jwtDecode, classNames, noop, wait, indexArray, indexArrayReadonly, isNonemptyArray, getReactTextElementText as getTitleText } from './utils'
export * from './typeUtils'
